import React from "react";
import { Popover, Tooltip } from "antd";
import {
  RiFileList3Line,
  RiReplyLine,
  RiEmotionLine,
  RiMapPin2Line,
  RiContactsBookLine,
  RiShoppingBag3Line,
  RiMegaphoneLine,
  RiInformationLine,
  RiQuestionLine,
} from "react-icons/ri";

import { formatWallTime, StatusIcon } from "./ChatUtils";
import { renderWhatsAppRichText } from "../../utils/whatsappRichText";
import EmojiPicker from "./EmojiPicker";

function ErrorPopoverContent({ err }) {
  if (!err) return null;

  return (
    <div style={{ maxWidth: 260 }}>
      <div style={{ fontWeight: 600, marginBottom: 4 }}>
        {err.title || "Error"}
      </div>

      <div style={{ fontSize: 12, color: "#555", marginBottom: 6 }}>
        {err.message || "Message failed"}
      </div>

      {err.details && (
        <div style={{ fontSize: 11, color: "#888", marginBottom: 6 }}>
          {err.details}
        </div>
      )}

      {err.code && (
        <div style={{ fontSize: 11, color: "#999" }}>Code: {err.code}</div>
      )}
    </div>
  );
}

export default function MessageBubble({ m, onReply, onReact }) {
  const out = m.direction === "outbound";
  const time = formatWallTime(m.timestamp);
  const isDeleted = String(m.status).toUpperCase() === "DELETED";
  const cls = out ? "sent" : "received";
  const wrapCls = out
    ? `msg-bubble-wrap msg-bubble-wrap-out ${isDeleted ? "msg-deleted" : ""}`
    : `msg-bubble-wrap msg-bubble-wrap-in ${isDeleted ? "msg-deleted" : ""}`;
  const isFailed = String(m.status).toUpperCase() === "FAILED";
  const errObj = m.error || null;
  const template = m.template || null;
  const context = m.context || null;
  const media = Array.isArray(m.media) && m.media.length ? m.media[0] : null;
  const reactions = Array.isArray(m.reactions) ? m.reactions : [];
  const mediaUrl = media?.cdn_url || media?.url || "";
  const mediaType = String(media?.type || "").toLowerCase();
  const entityType = String(m.entity_type || "").toLowerCase();
  const displayText = entityType === "sticker" && mediaUrl ? "" : (m.text || "");
  const contextPayload = context?.payload;
  const locationPayload = entityType === "location" ? contextPayload : null;
  const locationHref =
    locationPayload?.latitude && locationPayload?.longitude
      ? `https://www.google.com/maps?q=${encodeURIComponent(`${locationPayload.latitude},${locationPayload.longitude}`)}`
      : "";
  const contactsPayload = entityType === "contacts" && Array.isArray(contextPayload)
    ? contextPayload
    : [];
  const orderPayload = entityType === "order" ? contextPayload : null;
  const referralPayload = entityType === "referral" ? contextPayload : null;
  const infoEntity = ["system", "unsupported", "button", "interactive", "sticker"].includes(entityType);
  const reactionText = reactions
    .map((reaction) => reaction?.emoji)
    .filter(Boolean)
    .join(" ");
  const reactionBadge = reactionText ? (
    <Tooltip title={reactionText}>
      <span className={`msg-reaction-badge ${out ? "msg-reaction-badge-out" : "msg-reaction-badge-in"}`}>
        {reactionText}
      </span>
    </Tooltip>
  ) : null;

  const mediaNode = mediaUrl && !isDeleted ? (
    <div className="msg-media">
      {mediaType === "image" && (
        <a href={mediaUrl} target="_blank" rel="noreferrer">
          <img src={mediaUrl} alt={media.filename || "Attachment"} />
        </a>
      )}
      {mediaType === "video" && (
        <video src={mediaUrl} controls preload="metadata" />
      )}
      {mediaType === "audio" && (
        <audio src={mediaUrl} controls />
      )}
      {mediaType === "sticker" && (
        <a href={mediaUrl} target="_blank" rel="noreferrer">
          <img src={mediaUrl} alt="Sticker" />
        </a>
      )}
      {!["image", "video", "audio", "sticker"].includes(mediaType) && (
        <a href={mediaUrl} target="_blank" rel="noreferrer">
          {media.filename || "View attachment"}
        </a>
      )}
    </div>
  ) : null;

  const richNode = !isDeleted ? (
    <>
      {locationPayload ? (
        <div className="msg-special-card">
          <RiMapPin2Line size={18} />
          <div>
            <div className="msg-special-title">{locationPayload.name || "Shared location"}</div>
            {locationPayload.address ? (
              <div className="msg-special-subtitle">{locationPayload.address}</div>
            ) : null}
            {locationHref ? (
              <a href={locationHref} target="_blank" rel="noreferrer">Open map</a>
            ) : null}
          </div>
        </div>
      ) : null}

      {contactsPayload.length ? (
        <div className="msg-special-card">
          <RiContactsBookLine size={18} />
          <div>
            <div className="msg-special-title">
              {contactsPayload.length === 1 ? "Contact card" : `${contactsPayload.length} contact cards`}
            </div>
            <div className="msg-special-subtitle">
              {contactsPayload
                .map((c) => c?.name?.formatted_name || c?.name?.first_name)
                .filter(Boolean)
                .join(", ") || "Shared contact"}
            </div>
          </div>
        </div>
      ) : null}

      {orderPayload ? (
        <div className="msg-special-card">
          <RiShoppingBag3Line size={18} />
          <div>
            <div className="msg-special-title">Order received</div>
            <div className="msg-special-subtitle">
              {Array.isArray(orderPayload.product_items)
                ? `${orderPayload.product_items.length} item${orderPayload.product_items.length === 1 ? "" : "s"}`
                : "WhatsApp order"}
            </div>
          </div>
        </div>
      ) : null}

      {referralPayload ? (
        <div className="msg-special-card">
          <RiMegaphoneLine size={18} />
          <div>
            <div className="msg-special-title">{referralPayload.headline || "Referral"}</div>
            <div className="msg-special-subtitle">{referralPayload.body || referralPayload.source_url || "Shared referral"}</div>
          </div>
        </div>
      ) : null}

      {infoEntity ? (
        <div className="msg-type-chip">
          {entityType === "unsupported" ? <RiQuestionLine size={13} /> : <RiInformationLine size={13} />}
          <span>{entityType || "message"}</span>
        </div>
      ) : null}
    </>
  ) : null;

  const bubble = (
    <div className={wrapCls}>
      <div className={`msg ${cls} ${isFailed ? "msg-failed" : ""} ${isDeleted ? "msg-deleted-content" : ""}`}>
        {!isDeleted && context && (
          <div className="msg-quote-preview">
            <div className="msg-quote-line" />
            <div className="msg-quote-content">
              {context.text || "Message"}
            </div>
          </div>
        )}
        {mediaNode}
        {richNode}
        <span className="msg-text-body">
          {isDeleted ? (
            <span style={{ fontStyle: "italic", color: "#999" }}>This message was deleted</span>
          ) : (
            renderWhatsAppRichText(displayText)
          )}
        </span>

        <div className="msg-meta-row">
          {!isDeleted && template?.name ? (
            <Tooltip title={template.name}>
              <span className="msg-template-icon" aria-label="Template message">
                <RiFileList3Line size={14} />
              </span>
            </Tooltip>
          ) : null}
          <small className="msg-time">{time}</small>

          {!isDeleted && (
            <Tooltip title="Reply">
              <RiReplyLine
                className="msg-bubble-action"
                size={14}
                onClick={() => onReply?.(m)}
              />
            </Tooltip>
          )}

          {!isDeleted && (
            <Popover
              content={<EmojiPicker onSelect={(emoji) => onReact?.(m, emoji)} />}
              trigger="click"
              placement="top"
              overlayClassName="reaction-popover"
            >
              <Tooltip title="React">
                 <RiEmotionLine
                   className="msg-bubble-action"
                   size={14}
                 />
              </Tooltip>
            </Popover>
          )}

          {out && (
            <span className="msg-status-icon">
              <StatusIcon status={m.status} />
            </span>
          )}
        </div>
        {!isDeleted && reactionBadge}
      </div>
    </div>
  );

  if (isFailed && errObj) {
    return (
      <Popover
        content={<ErrorPopoverContent err={errObj} />}
        trigger="hover"
        placement="topRight"
      >
        <div style={{ cursor: "pointer" }}>{bubble}</div>
      </Popover>
    );
  }

  return bubble;
}
